import React, {useContext, useEffect, useState} from "react";
import TrackVASContext from "../../TrackVASContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {useAuth} from "../../modules/auth";
import axios from "axios";
import {ENTITY_ID_BRANCH} from "../generic/Constants";
import {useNavigate} from "react-router-dom";
import {BranchCard} from "../jobCards/BranchCard";

export function AttendanceBranchesPage() {
    const myData = useContext(TrackVASContext);
    const {currentUser} = useAuth();
    const navigate = useNavigate();
    const [branches, setBranches] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [showAllBranches, setShowAllBranches] = useState(false);

    // Helper functions to get dates
    const getToday = () => new Date().toISOString().split('T')[0];
    const getYesterday = () => {
        const date = new Date();
        date.setDate(date.getDate() - 1);
        return date.toISOString().split('T')[0];
    };
    const getDayBeforeYesterday = () => {
        const date = new Date();
        date.setDate(date.getDate() - 2);
        return date.toISOString().split('T')[0];
    };

    const formatDisplayDate = (dateStr) => {
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    useEffect(() => {
        myData.setBaseData({
            toolBarButtons: [],
        });

        return () => {
            myData.setBaseData({
                toolBarButtons: []
            });
        };
    }, []);

    useEffect(() => {
        if (currentUser?.userId) {
            fetchBranches();
        }
    }, [currentUser?.userId, showAllBranches]);

    const fetchBranches = () => {
        setIsLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL;

        // Build params conditionally based on showAllBranches
        const params = {
            entityId: ENTITY_ID_BRANCH,
            page: 1,
            sizePerPage: 100,
            currentIndex: 0,
            sortBy: 'name'
        };

        // Admin and Finance users always see all branches
        const isAdminOrFinance = currentUser.dashboardType === 'ADMIN_DASHBOARD' ||
                                 currentUser.dashboardType === 'FINANCE_DASHBOARD';

        // Only add role-based filter if not showing all branches AND not admin/finance
        if (!showAllBranches && !isAdminOrFinance) {
            params.fixedFilterData = currentUser.userId;

            // Filter by role: Manager or Supervisor
            if (currentUser.dashboardType === 'MANAGER_DASHBOARD') {
                params.fixedFilterColumn = 'managerId';
            } else if (currentUser.dashboardType === 'SUPERVISOR_DASHBOARD') {
                params.fixedFilterColumn = 'supervisorId';
            } else {
                // Default to supervisor for backward compatibility
                params.fixedFilterColumn = 'supervisorId';
            }
        }

        axios.get(`${apiUrl}/api/generic/all`, { params })
            .then(({data}) => {
                const branchList = data.entities || [];
                setBranches(branchList);
                setIsLoading(false);
            }).catch((error) => {
                setBranches([]);
                setIsLoading(false);
            });
    };

    const handleBranchClick = (branch) => {
        // Navigate to attendance worker page with selected date
        navigate(`/attendance/workers/${branch.id}`, {
            state: {
                branchId: branch.id,
                branchName: branch.name,
                dealerName: branch.dealerName,
                selectedDate: selectedDate
            }
        });
    };

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Attendance - Select Branch</PageTitle>
            <div className="card card-body">
                {/* Date Selector with Branch Filter Toggle */}
                <div className="mb-6">
                    {/* Header with Date and Branch Filter */}
                    <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 gap-3">
                        <div className="d-flex align-items-center">
                            <i className="bi bi-calendar3 fs-2 text-primary me-3"></i>
                            <label className="form-label fw-bold mb-0">Select Date</label>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            {/* Only show toggle for Manager/Supervisor users */}
                            {currentUser.dashboardType !== 'ADMIN_DASHBOARD' && currentUser.dashboardType !== 'FINANCE_DASHBOARD' && (
                                <button
                                    className={`btn btn-sm ${showAllBranches ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => setShowAllBranches(!showAllBranches)}
                                >
                                    <i className={`bi ${showAllBranches ? 'bi-building-check' : 'bi-person-badge'} me-1`}></i>
                                    {showAllBranches ? 'All Branches' : 'My Branches'}
                                </button>
                            )}
                            <button
                                className="btn btn-sm btn-success"
                                onClick={() => navigate('/attendance/month-view')}
                            >
                                <i className="bi bi-calendar-month me-1"></i>
                                View Month Attendance
                            </button>
                        </div>
                    </div>

                    {/* Date Display */}
                    <div className="mb-4 d-flex align-items-center">
                        <i className="bi bi-calendar-event fs-4 text-gray-600 me-3"></i>
                        <span className="fs-3 fw-bold text-gray-900">{formatDisplayDate(selectedDate)}</span>
                    </div>

                    {/* Quick Date Buttons */}
                    <div className="d-flex flex-wrap gap-3">
                        <button
                            className={`btn ${selectedDate === getToday() ? 'btn-primary' : 'btn-light-primary'}`}
                            onClick={() => {
                                setSelectedDate(getToday());
                                setShowCustomDatePicker(false);
                            }}
                        >
                            <i className="bi bi-calendar-check me-2"></i>
                            Today
                        </button>

                        <button
                            className={`btn ${selectedDate === getYesterday() ? 'btn-primary' : 'btn-light-primary'}`}
                            onClick={() => {
                                setSelectedDate(getYesterday());
                                setShowCustomDatePicker(false);
                            }}
                        >
                            <i className="bi bi-calendar-minus me-2"></i>
                            Yesterday
                        </button>

                        <button
                            className={`btn ${selectedDate === getDayBeforeYesterday() ? 'btn-primary' : 'btn-light-primary'}`}
                            onClick={() => {
                                setSelectedDate(getDayBeforeYesterday());
                                setShowCustomDatePicker(false);
                            }}
                        >
                            <i className="bi bi-calendar-minus me-2"></i>
                            Day Before Yesterday
                        </button>

                        <button
                            className={`btn ${showCustomDatePicker ? 'btn-primary' : 'btn-light-primary'}`}
                            onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
                        >
                            <i className="bi bi-calendar3 me-2"></i>
                            Other Date
                        </button>
                    </div>

                    {/* Custom Date Picker */}
                    {showCustomDatePicker && (
                        <div className="mt-4">
                            <input
                                type="date"
                                className="form-control form-control-solid"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                style={{maxWidth: '250px'}}
                            />
                        </div>
                    )}
                </div>

                <div className="separator separator-dashed my-6"></div>

                {/* Branch Cards */}
                {isLoading && (
                    <div className="text-center py-10">
                        <span className="spinner-border spinner-border-lg"></span>
                        <p className="mt-3">Loading your branches...</p>
                    </div>
                )}

                {!isLoading && branches.length === 0 && (
                    <div className="alert alert-light d-flex align-items-center p-5">
                        <i className="bi bi-info-circle fs-2x text-primary me-4"></i>
                        <div className="d-flex flex-column">
                            <h5 className="mb-1">No branches found</h5>
                            <span>You are not assigned as manager or supervisor to any branches.</span>
                        </div>
                    </div>
                )}

                {!isLoading && branches.length > 0 && (
                    <>
                        <h5 className="fw-bolder text-dark mb-5">
                            <i className="bi bi-diagram-3 fs-3 text-primary me-2"></i>
                            {(currentUser.dashboardType === 'ADMIN_DASHBOARD' || currentUser.dashboardType === 'FINANCE_DASHBOARD')
                                ? 'All Branches'
                                : (showAllBranches ? 'All Branches' : 'Your Branches')} ({branches.length})
                        </h5>
                        <div className="row">
                            {branches.map((branch) => (
                                <BranchCard
                                    key={branch.id}
                                    branch={branch}
                                    onBranchClick={handleBranchClick}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}